import React, { useState, useEffect, useRef, createContext, useContext } from 'react';
import { OC, FONT, isAdminRole } from '../constants/index.js';
import { useTheme } from '../constants/index.js';
import { useStore } from '../store/StoreProvider.js';
import OCApi from '../api/client.js';
import { Button, Badge, Icon, Input, Card } from '../components/ui.js';
import { LoadingState, ErrorState, EmptyState } from '../components/AsyncState.js';
import { AdminContentTab } from './adminContentTab.js';
import { StagedQueueItem } from '../components/StagedQueueItem.js';
import { isWhatsAppStagedItem } from '../lib/stagedWhatsApp.js';

function AdminReviewScreen() {
    const s = useStore();
    const t = useTheme(s.theme);
    const [tab, setTab] = useState('queue');
    const [queue, setQueue] = useState([]);
    const [sources, setSources] = useState([]);
    const [employerProfiles, setEmployerProfiles] = useState([]);
    const [adminUsers, setAdminUsers] = useState([]);
    const [usersTotal, setUsersTotal] = useState(0);
    const [usersRoleFilter, setUsersRoleFilter] = useState('all');
    const [ingestionRuns, setIngestionRuns] = useState([]);
    const [ingestionSummary, setIngestionSummary] = useState([]);
    const [ingestionSourceFilter, setIngestionSourceFilter] = useState('all');
    const [stats, setStats] = useState({ pendingReview: 0, liveOpportunities: 0, pendingEmployers: 0, totalUsers: 0, activeSources: 0 });
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState({});
    const [busyId, setBusyId] = useState(null);
    const [showSourceForm, setShowSourceForm] = useState(false);
    const [communityChannels, setCommunityChannels] = useState([]);
    const [reviewerApps, setReviewerApps] = useState([]);
    const [adminGuides, setAdminGuides] = useState([]);
    const [newsletterSubscribers, setNewsletterSubscribers] = useState([]);
    const [adminTestimonials, setAdminTestimonials] = useState([]);
    const [adminChapters, setAdminChapters] = useState([]);
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [createUserForm, setCreateUserForm] = useState({
        email: '',
        password: '',
        displayName: '',
        role: 'user',
    });
    const [sourceForm, setSourceForm] = useState({
        name: '',
        slug: '',
        type: 'rss',
        feedUrl: '',
        boardToken: '',
        provider: 'adzuna',
        country: 'gb',
        what: '',
        where: '',
        requiresReview: true,
    });
    const [waIntegration, setWaIntegration] = useState(null);
    const [waForm, setWaForm] = useState({
        enabled: false,
        apiBaseUrl: '',
        apiKey: '',
        useMock: true,
        pollIntervalMinutes: 5,
        requiresReview: true,
    });

    const loadAll = async (overrides = {}) => {
        setLoading(true);
        try {
            const roleFilter = overrides.usersRoleFilter ?? usersRoleFilter;
            const sourceFilter = overrides.ingestionSourceFilter ?? ingestionSourceFilter;
            const userParams = roleFilter === 'all' ? { limit: 50 } : { limit: 50, role: roleFilter };
            const runParams = {
                limit: 50,
                ...(sourceFilter !== 'all' ? { sourceId: Number(sourceFilter) } : {}),
            };
            const [stagedRes, platformRes, srcRes, employerRes, usersRes, runsRes, ingestionStatsRes, chRes, revRes, guidesRes, newsletterRes, testimonialsRes, chaptersRes, waRes] = await Promise.all([
                OCApi.getPendingStaged({ limit: 100 }),
                OCApi.getAdminPlatformStats(),
                OCApi.getAdminSources(),
                OCApi.getAdminEmployerProfiles({ status: 'pending' }),
                OCApi.getAdminUsers(userParams),
                OCApi.getAdminIngestionRuns(runParams),
                OCApi.getAdminIngestionStats({ limit: 10 }),
                OCApi.getAdminCommunityChannels(),
                OCApi.getAdminReviewerApplications({ status: 'pending' }),
                OCApi.getAdminResourceGuides(),
                OCApi.getAdminNewsletterSubscribers(),
                OCApi.getAdminTestimonials(),
                OCApi.getAdminUniversityChapters(),
                OCApi.getWhatsAppIntegration().catch(() => ({ integration: null })),
            ]);
            setQueue(stagedRes.staged || []);
            setSources(srcRes.sources || []);
            setEmployerProfiles(employerRes.profiles || []);
            setAdminUsers(usersRes.users || []);
            setUsersTotal(usersRes.total || 0);
            setIngestionRuns(runsRes.runs || []);
            setIngestionSummary(ingestionStatsRes.summary || []);
            setStats(platformRes.stats || { pendingReview: 0, liveOpportunities: 0, pendingEmployers: 0, totalUsers: 0, activeSources: 0 });
            setCommunityChannels(chRes.channels || []);
            setReviewerApps(revRes.applications || []);
            setAdminGuides(guidesRes.guides || []);
            setNewsletterSubscribers(newsletterRes.subscribers || []);
            setAdminTestimonials(testimonialsRes.testimonials || []);
            setAdminChapters(chaptersRes.chapters || []);
            const wa = waRes.integration || null;
            setWaIntegration(wa);
            if (wa) {
                setWaForm({
                    enabled: Boolean(wa.enabled),
                    apiBaseUrl: wa.config?.apiBaseUrl || '',
                    apiKey: '',
                    useMock: wa.config?.useMock !== false,
                    pollIntervalMinutes: wa.config?.pollIntervalMinutes || 5,
                    requiresReview: wa.requiresReview !== false,
                });
            }
            setSelected({});
        } catch (err) {
            s.showToast(err.message || 'Could not load admin data', 'x');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!s.authReady) return;
        if (!s.authed || !isAdminRole(s.user.role)) {
            s.showToast('Admin access required', 'shieldCheck');
            s.go('login');
            return;
        }
        loadAll();
    }, [s.authReady, s.authed, s.user.role]);

    const toggleSelect = (id) => {
        setSelected(prev => {
            const next = { ...prev };
            if (next[id]) delete next[id];
            else next[id] = true;
            return next;
        });
    };

    const approveOne = async (id) => {
        setBusyId(id);
        try {
            await OCApi.approveStaged(id);
            s.showToast('Approved and published', 'check');
            await loadAll();
            await s.loadOpportunities({ limit: 24 });
        } catch (err) {
            s.showToast(err.message || 'Approve failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const rejectOne = async (id) => {
        setBusyId(id);
        try {
            await OCApi.rejectStaged(id, 'Rejected from admin console');
            s.showToast('Rejected', 'x');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Reject failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const approveSelected = async () => {
        const ids = Object.keys(selected).map(Number);
        if (!ids.length) return;
        setBusyId('bulk');
        try {
            const res = await OCApi.bulkApproveStaged(ids);
            s.showToast(`Approved ${res.approvedCount || 0} item(s)`, 'check');
            await loadAll();
            await s.loadOpportunities({ limit: 24 });
        } catch (err) {
            s.showToast(err.message || 'Bulk approve failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const toggleSourceReview = async (source) => {
        setBusyId(`src-${source.id}`);
        try {
            await OCApi.updateSource(source.id, { requiresReview: !source.requiresReview });
            s.showToast(
                source.requiresReview ? 'Source set to auto-publish' : 'Source set to manual review',
                'shieldCheck'
            );
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Update failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const runSourceNow = async (sourceId) => {
        setBusyId(`run-${sourceId}`);
        try {
            const res = await OCApi.runSource(sourceId);
            const r = res.result?.result || {};
            const msg = r.autoPromoted
                ? `Ingested · auto-published ${r.autoPromoted}`
                : `Ingested · staged ${r.staged || 0}`;
            s.showToast(msg, 'check');
            await loadAll();
            if (r.autoPromoted) await s.loadOpportunities({ limit: 24 });
        } catch (err) {
            s.showToast(err.message || 'Ingestion failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const verifyEmployer = async (userId) => {
        setBusyId(`employer-verify-${userId}`);
        try {
            await OCApi.verifyAdminEmployerProfile(userId);
            s.showToast('Employer verified', 'check');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Verify failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const rejectEmployer = async (userId) => {
        setBusyId(`employer-reject-${userId}`);
        try {
            await OCApi.rejectAdminEmployerProfile(userId, 'Needs more company information');
            s.showToast('Employer profile rejected', 'x');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Reject failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const createSource = async () => {
        setBusyId('create-source');
        try {
            const payload = OCApi.buildSourcePayload(sourceForm);
            const jobsCat = s.categories.find(c => c.slug === 'jobs') || s.categories[0];
            if (jobsCat) payload.categoryId = jobsCat.id;
            await OCApi.createSource(payload);
            s.showToast('Source created', 'check');
            setShowSourceForm(false);
            setSourceForm({
                name: '',
                slug: '',
                type: 'rss',
                feedUrl: '',
                boardToken: '',
                provider: 'adzuna',
                country: 'gb',
                what: '',
                where: '',
                requiresReview: true,
            });
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Create source failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const deactivateSource = async (source) => {
        setBusyId(`deactivate-${source.id}`);
        try {
            await OCApi.deleteSource(source.id);
            s.showToast('Source deactivated', 'check');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Deactivate failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const reactivateSource = async (source) => {
        setBusyId(`reactivate-${source.id}`);
        try {
            await OCApi.updateSource(source.id, { isActive: true });
            s.showToast('Source reactivated', 'check');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Reactivate failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const updateUserRole = async (user, role) => {
        setBusyId(`user-role-${user.id}`);
        try {
            await OCApi.updateAdminUser(user.id, { role });
            s.showToast('User role updated', 'check');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Role update failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const toggleUserActive = async (user) => {
        setBusyId(`user-active-${user.id}`);
        try {
            await OCApi.updateAdminUser(user.id, { isActive: !user.isActive });
            s.showToast(user.isActive ? 'User deactivated' : 'User activated', 'check');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Update failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const createUser = async () => {
        const { email, password, displayName, role } = createUserForm;
        if (!email.trim() || !password || password.length < 6) {
            s.showToast('Email and password (min 6 chars) required', 'x');
            return;
        }
        setBusyId('create-user');
        try {
            await OCApi.createAdminUser({
                email: email.trim(),
                password,
                displayName: displayName.trim() || undefined,
                role,
                emailVerified: true,
            });
            s.showToast('User created', 'check');
            setCreateUserForm({ email: '', password: '', displayName: '', role: 'user' });
            setShowCreateUser(false);
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Create user failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const adminInputStyle = {
        width: '100%',
        boxSizing: 'border-box',
        border: `1px solid ${t.border.base}`,
        borderRadius: 10,
        padding: '10px 12px',
        background: t.bg.page,
        color: t.text.primary,
        fontFamily: FONT.body,
        fontSize: 13.5,
    };

    const ingestableSources = sources.filter(src => ['rss', 'ats', 'free_api', 'paid_api', 'whatsapp_platform'].includes(src.type));

    const saveWhatsAppIntegration = async () => {
        setBusyId('wa-save');
        try {
            const payload = {
                enabled: waForm.enabled,
                apiBaseUrl: waForm.apiBaseUrl || null,
                useMock: waForm.useMock,
                pollIntervalMinutes: Number(waForm.pollIntervalMinutes) || 5,
                requiresReview: waForm.requiresReview,
            };
            if (waForm.apiKey) payload.apiKey = waForm.apiKey;
            await OCApi.updateWhatsAppIntegration(payload);
            s.showToast('WhatsApp integration saved', 'check');
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Could not save WhatsApp settings', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const testWhatsAppConnection = async () => {
        setBusyId('wa-test');
        try {
            const res = await OCApi.testWhatsAppIntegration();
            s.showToast(res.message || 'Connection OK', 'check');
        } catch (err) {
            s.showToast(err.message || 'Connection failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const runWhatsAppIngestNow = async () => {
        setBusyId('wa-ingest');
        try {
            const res = await OCApi.runWhatsAppIngest();
            const r = res.result?.result;
            s.showToast(
                r ? `Fetched ${r.fetched}, staged ${r.staged}, skipped ${r.skipped}` : 'Ingest complete',
                'check'
            );
            await loadAll();
        } catch (err) {
            s.showToast(err.message || 'Ingest failed', 'x');
        } finally {
            setBusyId(null);
        }
    };

    const cardStyle = {
        background: t.bg.card,
        border: `1px solid ${t.border.base}`,
        borderRadius: 14,
        padding: 20,
    };

    return React.createElement('div', {
        style: { maxWidth: 1100, margin: '0 auto', padding: '32px 24px 64px' }
    },
        React.createElement('div', { style: { marginBottom: 24 } },
            React.createElement('div', {
                style: { fontFamily: FONT.mono, fontSize: 11, color: t.text.secondary, letterSpacing: '0.12em', textTransform: 'uppercase' }
            }, 'Admin console'),
            React.createElement('h1', {
                style: { fontFamily: FONT.display, fontWeight: 800, fontSize: 32, margin: '8px 0 6px', color: t.text.primary }
            }, 'Platform admin'),
            React.createElement('p', { style: { color: t.text.secondary, margin: 0 } },
                'Review listings, manage sources, users, employers, and ingestion history.'
            )
        ),
        React.createElement('div', {
            style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 20 }
        },
            [
                ['Pending review', stats.pendingReview || queue.length],
                ['Live opportunities', stats.liveOpportunities || 0],
                ['Total users', stats.totalUsers || adminUsers.length],
                ['Pending employers', stats.pendingEmployers || employerProfiles.length],
                ['Active sources', stats.activeSources || sources.filter(x => x.isActive).length],
            ].map(([label, value]) =>
                React.createElement('div', { key: label, style: cardStyle },
                    React.createElement('div', { style: { fontSize: 12, color: t.text.secondary } }, label),
                    React.createElement('div', { style: { fontFamily: FONT.display, fontSize: 28, fontWeight: 800, color: t.text.primary } }, value)
                )
            )
        ),
        React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' } },
            [
                ['queue', 'Review queue'],
                ['employers', 'Employers'],
                ['sources', 'Sources'],
                ['users', 'Users'],
                ['content', 'Community & guides'],
                ['ingestion', 'Ingestion'],
            ].map(([name, label]) =>
                React.createElement('button', {
                    key: name,
                    onClick: () => setTab(name),
                    style: {
                        border: `1px solid ${tab === name ? OC.orange : t.border.base}`,
                        background: tab === name ? (s.theme === 'dark' ? 'rgba(249,115,22,.14)' : OC.orangeMuted) : 'transparent',
                        color: tab === name ? OC.orange : t.text.secondary,
                        borderRadius: 999,
                        padding: '8px 14px',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: 13,
                    }
                }, label)
            ),
            React.createElement('button', {
                onClick: loadAll,
                style: {
                    marginLeft: 'auto', border: `1px solid ${t.border.base}`, background: 'transparent',
                    borderRadius: 999, padding: '8px 14px', cursor: 'pointer', color: t.text.secondary, fontSize: 13,
                }
            }, 'Refresh')
        ),
        loading ? React.createElement('div', { style: { color: t.text.secondary, padding: 24 } }, 'Loading…') :
        tab === 'queue' ? React.createElement('div', null,
            queue.length ? React.createElement('div', { style: { marginBottom: 12 } },
                React.createElement(Button, {
                    kind: 'primary', size: 'sm', theme: s.theme,
                    onClick: approveSelected,
                    disabled: busyId === 'bulk' || !Object.keys(selected).length,
                }, `Approve selected (${Object.keys(selected).length})`)
            ) : null,
            queue.length ? React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginBottom: 10 } },
                `${queue.filter(isWhatsAppStagedItem).length} from WhatsApp · ${queue.length} total pending`
            ) : null,
            queue.length ? queue.map(item =>
                React.createElement(StagedQueueItem, {
                    key: item.id,
                    item,
                    theme: s.theme,
                    t,
                    cardStyle,
                    selected: selected[item.id],
                    onToggleSelect: () => toggleSelect(item.id),
                    onApprove: () => approveOne(item.id),
                    onReject: () => rejectOne(item.id),
                    busyId,
                })
            ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } }, 'No pending items — queue is clear.')
        ) :
        tab === 'employers' ? React.createElement('div', null,
            employerProfiles.length ? employerProfiles.map(row =>
                React.createElement('div', {
                    key: row.userId,
                    style: { ...cardStyle, marginBottom: 12, display: 'grid', gridTemplateColumns: '1fr auto', gap: 14, alignItems: 'start' }
                },
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontWeight: 700, fontSize: 16, color: t.text.primary } }, row.companyName),
                        React.createElement('div', { style: { fontSize: 13, color: t.text.secondary, marginTop: 4 } },
                            [row.displayName || row.userEmail, row.industry, row.headquarters].filter(Boolean).join(' · ')
                        ),
                        row.about ? React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 8, lineHeight: 1.5 } }, row.about.slice(0, 180), row.about.length > 180 ? '…' : '') : null
                    ),
                    React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
                        React.createElement(Button, {
                            kind: 'primary', size: 'sm', theme: s.theme,
                            onClick: () => verifyEmployer(row.userId),
                            disabled: busyId === `employer-verify-${row.userId}`,
                        }, 'Verify'),
                        React.createElement(Button, {
                            kind: 'secondary', size: 'sm', theme: s.theme,
                            onClick: () => rejectEmployer(row.userId),
                            disabled: busyId === `employer-reject-${row.userId}`,
                        }, 'Reject')
                    )
                )
            ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } }, 'No pending employer profiles.')
        ) : tab === 'sources' ? React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' } },
                React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } }, `${ingestableSources.length} ingestable source(s)`),
                React.createElement(Button, {
                    kind: 'secondary', size: 'sm', theme: s.theme,
                    onClick: () => setShowSourceForm(v => !v),
                }, showSourceForm ? 'Cancel' : 'Add source')
            ),
            showSourceForm ? React.createElement('div', { style: { ...cardStyle, marginBottom: 14 } },
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 } },
                    React.createElement('input', {
                        placeholder: 'Source name',
                        value: sourceForm.name,
                        onChange: e => setSourceForm(f => ({ ...f, name: e.target.value })),
                        style: adminInputStyle,
                    }),
                    React.createElement('input', {
                        placeholder: 'slug-kebab-case',
                        value: sourceForm.slug,
                        onChange: e => setSourceForm(f => ({ ...f, slug: e.target.value })),
                        style: adminInputStyle,
                    }),
                    React.createElement('select', {
                        value: sourceForm.type,
                        onChange: e => setSourceForm(f => ({ ...f, type: e.target.value })),
                        style: adminInputStyle,
                    },
                        ['rss', 'ats', 'free_api', 'paid_api'].map(type =>
                            React.createElement('option', { key: type, value: type }, type)
                        )
                    )
                ),
                sourceForm.type === 'rss' ? React.createElement('input', {
                    placeholder: 'RSS feed URL',
                    value: sourceForm.feedUrl,
                    onChange: e => setSourceForm(f => ({ ...f, feedUrl: e.target.value })),
                    style: { ...adminInputStyle, marginBottom: 12 },
                }) : null,
                sourceForm.type === 'ats' ? React.createElement('input', {
                    placeholder: 'Greenhouse board token',
                    value: sourceForm.boardToken,
                    onChange: e => setSourceForm(f => ({ ...f, boardToken: e.target.value })),
                    style: { ...adminInputStyle, marginBottom: 12 },
                }) : null,
                (sourceForm.type === 'free_api' || sourceForm.type === 'paid_api') ? React.createElement('div', {
                    style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, marginBottom: 12 }
                },
                    React.createElement('select', {
                        value: sourceForm.provider,
                        onChange: e => setSourceForm(f => ({ ...f, provider: e.target.value })),
                        style: adminInputStyle,
                    },
                        ['adzuna', 'jooble', 'usajobs'].map(provider =>
                            React.createElement('option', { key: provider, value: provider }, provider)
                        )
                    ),
                    React.createElement('input', {
                        placeholder: 'Country code (gb)',
                        value: sourceForm.country,
                        onChange: e => setSourceForm(f => ({ ...f, country: e.target.value })),
                        style: adminInputStyle,
                    }),
                    React.createElement('input', {
                        placeholder: 'Keywords / what',
                        value: sourceForm.what,
                        onChange: e => setSourceForm(f => ({ ...f, what: e.target.value })),
                        style: adminInputStyle,
                    }),
                    React.createElement('input', {
                        placeholder: 'Location / where',
                        value: sourceForm.where,
                        onChange: e => setSourceForm(f => ({ ...f, where: e.target.value })),
                        style: adminInputStyle,
                    })
                ) : null,
                React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: t.text.secondary, marginBottom: 12 } },
                    React.createElement('input', {
                        type: 'checkbox',
                        checked: sourceForm.requiresReview !== false,
                        onChange: e => setSourceForm(f => ({ ...f, requiresReview: e.target.checked })),
                    }),
                    'Require manual review before publish'
                ),
                React.createElement(Button, {
                    kind: 'primary', size: 'sm', theme: s.theme,
                    onClick: createSource,
                    disabled: busyId === 'create-source' || !sourceForm.name || !sourceForm.slug,
                }, busyId === 'create-source' ? 'Creating…' : 'Create source')
            ) : null,
            ingestableSources.length ? ingestableSources.map(src =>
                React.createElement('div', {
                    key: src.id,
                    style: { ...cardStyle, marginBottom: 12, display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'center' }
                },
                    React.createElement('div', null,
                        React.createElement('div', { style: { fontWeight: 700, color: t.text.primary } }, src.name),
                        React.createElement('div', { style: { fontSize: 12.5, color: t.text.secondary, marginTop: 4 } },
                            `${src.type} · ${src.slug} · ${src.isActive ? (src.requiresReview === false ? 'Auto-publish' : 'Manual review') : 'Inactive'} · last: ${src.lastStatus || 'never'}`
                        )
                    ),
                    React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
                        src.isActive ? React.createElement(Button, {
                            kind: 'secondary', size: 'sm', theme: s.theme,
                            onClick: () => toggleSourceReview(src),
                            disabled: busyId === `src-${src.id}`,
                        }, src.requiresReview === false ? 'Require review' : 'Auto-publish') : null,
                        src.isActive ? React.createElement(Button, {
                            kind: 'primary', size: 'sm', theme: s.theme,
                            onClick: () => runSourceNow(src.id),
                            disabled: busyId === `run-${src.id}`,
                        }, 'Run now') : null,
                        src.isActive ? React.createElement(Button, {
                            kind: 'ghost', size: 'sm', theme: s.theme,
                            onClick: () => deactivateSource(src),
                            disabled: busyId === `deactivate-${src.id}`,
                        }, 'Deactivate') : React.createElement(Button, {
                            kind: 'secondary', size: 'sm', theme: s.theme,
                            onClick: () => reactivateSource(src),
                            disabled: busyId === `reactivate-${src.id}`,
                        }, 'Reactivate')
                    )
                )
            ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } }, 'No ingestable sources configured.')
        ) : tab === 'users' ? React.createElement('div', null,
            React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' } },
                React.createElement('select', {
                    value: usersRoleFilter,
                    onChange: e => {
                        const value = e.target.value;
                        setUsersRoleFilter(value);
                        loadAll({ usersRoleFilter: value });
                    },
                    style: adminInputStyle,
                },
                    ['all', 'user', 'employer', 'admin', 'super_admin'].map(role =>
                        React.createElement('option', { key: role, value: role }, role === 'all' ? 'All roles' : role)
                    )
                ),
                React.createElement('div', { style: { fontSize: 13, color: t.text.secondary } }, `${usersTotal} user(s)`),
                React.createElement(Button, {
                    kind: 'secondary', size: 'sm', theme: s.theme,
                    onClick: () => setShowCreateUser(v => !v),
                }, showCreateUser ? 'Cancel' : 'Create user')
            ),
            showCreateUser ? React.createElement('div', { style: { ...cardStyle, marginBottom: 16 } },
                React.createElement('div', { style: { fontWeight: 700, marginBottom: 12, color: t.text.primary } }, 'New user'),
                React.createElement('div', { style: { display: 'grid', gap: 10, maxWidth: 420 } },
                    React.createElement(Input, {
                        theme: s.theme,
                        placeholder: 'Email',
                        value: createUserForm.email,
                        onChange: email => setCreateUserForm(f => ({ ...f, email })),
                    }),
                    React.createElement(Input, {
                        theme: s.theme,
                        type: 'password',
                        placeholder: 'Password (min 6 characters)',
                        value: createUserForm.password,
                        onChange: password => setCreateUserForm(f => ({ ...f, password })),
                    }),
                    React.createElement(Input, {
                        theme: s.theme,
                        placeholder: 'Display name (optional)',
                        value: createUserForm.displayName,
                        onChange: displayName => setCreateUserForm(f => ({ ...f, displayName })),
                    }),
                    React.createElement('select', {
                        value: createUserForm.role,
                        onChange: e => setCreateUserForm(f => ({ ...f, role: e.target.value })),
                        style: adminInputStyle,
                    },
                        ['user', 'employer', 'admin', ...(s.user?.role === 'super_admin' ? ['super_admin'] : [])].map(role =>
                            React.createElement('option', { key: role, value: role }, role)
                        )
                    ),
                    React.createElement(Button, {
                        kind: 'primary', size: 'sm', theme: s.theme,
                        onClick: createUser,
                        disabled: busyId === 'create-user',
                    }, busyId === 'create-user' ? 'Creating…' : 'Create account')
                )
            ) : null,
            adminUsers.length ? React.createElement(Card, { theme: s.theme, padding: 0, style: { overflow: 'hidden' } },
                React.createElement('div', { style: { overflowX: 'auto' } },
                    React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontFamily: FONT.body } },
                        React.createElement('thead', null,
                            React.createElement('tr', { style: { background: t.bg.subtle, borderBottom: `1px solid ${t.border.base}` } },
                                ['User', 'Role', 'Status', 'Joined', 'Actions'].map(h =>
                                    React.createElement('th', { key: h, style: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: t.text.secondary, textTransform: 'uppercase' } }, h)
                                )
                            )
                        ),
                        React.createElement('tbody', null,
                            adminUsers.map(user =>
                                React.createElement('tr', { key: user.id, style: { borderBottom: `1px solid ${t.border.base}` } },
                                    React.createElement('td', { style: { padding: '12px 16px' } },
                                        React.createElement('div', { style: { fontWeight: 600, color: t.text.primary } }, user.displayName || user.email),
                                        React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginTop: 2 } }, user.email)
                                    ),
                                    React.createElement('td', { style: { padding: '12px 16px' } },
                                        React.createElement('select', {
                                            value: user.role,
                                            disabled: busyId === `user-role-${user.id}` || user.email === s.user?.email,
                                            onChange: e => updateUserRole(user, e.target.value),
                                            style: adminInputStyle,
                                        },
                                            ['user', 'employer', 'admin', 'super_admin'].map(role =>
                                                React.createElement('option', { key: role, value: role }, role)
                                            )
                                        )
                                    ),
                                    React.createElement('td', { style: { padding: '12px 16px' } },
                                        React.createElement(Badge, {
                                            color: user.isActive ? OC.success : OC.neutral,
                                            theme: s.theme,
                                        }, user.isActive ? 'Active' : 'Inactive')
                                    ),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary, fontSize: 13 } },
                                        user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '—'
                                    ),
                                    React.createElement('td', { style: { padding: '12px 16px' } },
                                        React.createElement(Button, {
                                            kind: user.isActive ? 'ghost' : 'secondary',
                                            size: 'sm',
                                            theme: s.theme,
                                            disabled: busyId === `user-active-${user.id}` || user.email === s.user?.email,
                                            onClick: () => toggleUserActive(user),
                                        }, user.isActive ? 'Deactivate' : 'Activate')
                                    )
                                )
                            )
                        )
                    )
                )
            ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } }, 'No users found.')
        ) : tab === 'content' ? React.createElement(AdminContentTab, {
            s, t, cardStyle, adminInputStyle, loadAll, busyId, setBusyId,
            communityChannels, adminGuides, reviewerApps, newsletterSubscribers,
            adminTestimonials, adminChapters,
        }) : React.createElement('div', null,
            waIntegration ? React.createElement('div', { style: { ...cardStyle, marginBottom: 16 } },
                React.createElement('div', { style: { fontWeight: 700, marginBottom: 8, color: t.text.primary } }, 'WhatsApp platform ingestor'),
                React.createElement('p', { style: { fontSize: 13, color: t.text.secondary, margin: '0 0 14px', lineHeight: 1.5 } },
                    'Pulls forwards that reached your Opportunity Circle channel from the WhatsApp automation platform. Items land in the review queue as staged listings.'
                ),
                React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 12, marginBottom: 12 } },
                    React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 } },
                        React.createElement('input', {
                            type: 'checkbox',
                            checked: waForm.enabled,
                            onChange: e => setWaForm(f => ({ ...f, enabled: e.target.checked })),
                        }),
                        'Enable scheduled ingest'
                    ),
                    React.createElement('label', { style: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 } },
                        React.createElement('input', {
                            type: 'checkbox',
                            checked: waForm.useMock,
                            onChange: e => setWaForm(f => ({ ...f, useMock: e.target.checked })),
                        }),
                        'Use mock API (dev)'
                    ),
                    React.createElement('input', {
                        type: 'number',
                        min: 1,
                        max: 120,
                        value: waForm.pollIntervalMinutes,
                        onChange: e => setWaForm(f => ({ ...f, pollIntervalMinutes: e.target.value })),
                        style: adminInputStyle,
                        placeholder: 'Poll interval (minutes)',
                    }),
                    React.createElement('input', {
                        value: waForm.apiBaseUrl,
                        onChange: e => setWaForm(f => ({ ...f, apiBaseUrl: e.target.value })),
                        style: adminInputStyle,
                        placeholder: 'Platform API URL (e.g. http://localhost:3000)',
                    }),
                    React.createElement('input', {
                        type: 'password',
                        value: waForm.apiKey,
                        onChange: e => setWaForm(f => ({ ...f, apiKey: e.target.value })),
                        style: adminInputStyle,
                        placeholder: waIntegration.config?.hasApiKey ? `Key set (${waIntegration.config.apiKeyMasked})` : 'Connection API key',
                    })
                ),
                waIntegration.config?.lastError ? React.createElement('div', {
                    style: { fontSize: 12.5, color: OC.urgent, marginBottom: 10 }
                }, `Last error: ${waIntegration.config.lastError}`) : null,
                React.createElement('div', { style: { fontSize: 12, color: t.text.secondary, marginBottom: 12 } },
                    `Last poll: ${waIntegration.config?.lastPollAt ? new Date(waIntegration.config.lastPollAt).toLocaleString() : '—'} · Last success: ${waIntegration.config?.lastSuccessAt ? new Date(waIntegration.config.lastSuccessAt).toLocaleString() : '—'}`
                ),
                React.createElement('div', { style: { display: 'flex', gap: 8, flexWrap: 'wrap' } },
                    React.createElement(Button, {
                        kind: 'primary', size: 'sm', theme: s.theme,
                        onClick: saveWhatsAppIntegration,
                        disabled: busyId === 'wa-save',
                    }, busyId === 'wa-save' ? 'Saving…' : 'Save'),
                    React.createElement(Button, {
                        kind: 'secondary', size: 'sm', theme: s.theme,
                        onClick: testWhatsAppConnection,
                        disabled: busyId === 'wa-test',
                    }, 'Test connection'),
                    React.createElement(Button, {
                        kind: 'secondary', size: 'sm', theme: s.theme,
                        onClick: runWhatsAppIngestNow,
                        disabled: busyId === 'wa-ingest',
                    }, busyId === 'wa-ingest' ? 'Running…' : 'Run ingest now')
                )
            ) : React.createElement('div', { style: { ...cardStyle, marginBottom: 16, color: t.text.secondary, fontSize: 13 } },
                'WhatsApp source not seeded. Run `node database/seeds/sources.seed.js` on the backend.'
            ),
            React.createElement('div', { style: { display: 'flex', gap: 8, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center' } },
                React.createElement('select', {
                    value: ingestionSourceFilter,
                    onChange: e => {
                        const value = e.target.value;
                        setIngestionSourceFilter(value);
                        loadAll({ ingestionSourceFilter: value });
                    },
                    style: adminInputStyle,
                },
                    React.createElement('option', { value: 'all' }, 'All sources'),
                    ingestableSources.map(src =>
                        React.createElement('option', { key: src.id, value: String(src.id) }, src.name)
                    )
                ),
                ingestionSummary.length ? ingestionSummary.map(row =>
                    React.createElement(Badge, {
                        key: row.status,
                        color: row.status === 'success' ? OC.success : (row.status === 'failed' ? OC.urgent : OC.warning),
                        theme: s.theme,
                    }, `${row.status}: ${row.count}`)
                ) : null
            ),
            ingestionRuns.length ? React.createElement(Card, { theme: s.theme, padding: 0, style: { overflow: 'hidden' } },
                React.createElement('div', { style: { overflowX: 'auto' } },
                    React.createElement('table', { style: { width: '100%', borderCollapse: 'collapse', fontFamily: FONT.body } },
                        React.createElement('thead', null,
                            React.createElement('tr', { style: { background: t.bg.subtle, borderBottom: `1px solid ${t.border.base}` } },
                                ['Source', 'Started', 'Status', 'Fetched', 'Staged', 'Promoted', 'Error'].map(h =>
                                    React.createElement('th', { key: h, style: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: t.text.secondary, textTransform: 'uppercase' } }, h)
                                )
                            )
                        ),
                        React.createElement('tbody', null,
                            ingestionRuns.map(run =>
                                React.createElement('tr', { key: run.id, style: { borderBottom: `1px solid ${t.border.base}` } },
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.primary } }, run.sourceName || run.sourceSlug || `#${run.sourceId}`),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary, fontSize: 13 } },
                                        run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'
                                    ),
                                    React.createElement('td', { style: { padding: '12px 16px' } },
                                        React.createElement(Badge, {
                                            color: run.status === 'success' ? OC.success : (run.status === 'failed' ? OC.urgent : OC.warning),
                                            theme: s.theme,
                                        }, run.status)
                                    ),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary } }, run.itemsFetched ?? '—'),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary } }, run.itemsStaged ?? '—'),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary } }, run.itemsPromoted ?? '—'),
                                    React.createElement('td', { style: { padding: '12px 16px', color: t.text.secondary, fontSize: 12, maxWidth: 280 } }, run.error || '—')
                                )
                            )
                        )
                    )
                )
            ) : React.createElement('div', { style: { ...cardStyle, color: t.text.secondary } }, 'No ingestion runs recorded yet.')
        )
    );
}

export { AdminReviewScreen };
